import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import leftArrow from "../../../../assests/leftArrow.svg";
import { 
  Button, 
  TextField, 
  CircularProgress, 
  Alert, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel,
  InputAdornment,
  Snackbar 
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import awaitingicon from "../../../../assests/awaitingIcon.svg"
import successIcon from "../../../../assests/checkIconGreen.svg"; // Add success icon
import "./uidDetails.css";
import moment from "moment-timezone"; // Import moment-timezone

// Import icons
import uidIcon from "../../../../assests/uid.svg";
import vmidIcon from "../../../../assests/vmids.svg";
import calendarIcon from "../../../../assests/calander.svg";
import userIcon from "../../../../assests/user-roles.svg";
import qrIcon from "../../../../assests/qr-icon.svg";
import locationIcon from "../../../../assests/locationIcon.svg";
import statusIcon from "../../../../assests/sloticon.svg";
import dropdownArrow from "../../../../assests/downArrow.png";
import searchIcon from "../../../../assests/search.svg";

// Import QR Code Modal
import QRCodeModal from "./QRCodeModal";

const UidDashboardDetailsComponent = ({
  uidDetails,
  loading,
  error,
  navigate,
  onUnmap,
  onMap,
  onConfirmedMap,
  onCancelMap,
  handleUidTimeline,
  vmList = [],
  selectedVmDetails,
  onVmSelect,
  onSearch,
  searchQuery,
  searchLoading,
  onSuccessNavigation,
  mapSuccess,
  unmapSuccess,
  resetSuccessStates,
  mapConfirmationOpen,
  pendingMapData
}) => {
  const { uid } = useParams();
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [selectedVmid, setSelectedVmid] = useState("");
  const [selectedUid, setSelectedUid] = useState(uid || "");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [unmapLoading, setUnmapLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [vmidSearch, setVmidSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchInputRef = useRef(null);
  
  // QR Code Modal States
  const [showQRModal, setShowQRModal] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isCapturing, setIsCapturing] = useState(false);

  // Success Dialog State
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [successType, setSuccessType] = useState(""); // "map" or "unmap"

  // Reset success states when component unmounts or mode changes
  useEffect(() => {
    return () => {
      if (resetSuccessStates) {
        resetSuccessStates();
      }
    };
  }, []);

  // Show success dialog when map or unmap is successful
  useEffect(() => {
    if (mapSuccess) {
      setSuccessMessage("UID mapped successfully!");
      setSuccessType("map");
      setShowSuccessDialog(true);
    } else if (unmapSuccess) {
      setSuccessMessage("UID unmapped successfully!");
      setSuccessType("unmap");
      setShowSuccessDialog(true);
    }
  }, [mapSuccess, unmapSuccess]);

  // Handle success dialog OK button click
  const handleSuccessOk = () => {
    setShowSuccessDialog(false);
    if (onSuccessNavigation) {
      onSuccessNavigation();
    }
  };

  // Handle reason input change with validation
  const handleReasonChange = (e) => {
    const value = e.target.value;
    setReason(value);
    
    // Validate reason length
    if (value.trim() === "") {
      setReasonError("Reason is required");
    } else if (value.length < 5) {
      setReasonError("Reason must be at least 5 characters long");
    } else if (value.length > 100) {
      setReasonError("Reason cannot exceed 100 characters");
    } else {
      setReasonError("");
    }
  };

  // Validate reason before proceeding
  const validateReason = () => {
    if (reason.trim() === "") {
      setReasonError("Reason is required");
      return false;
    } else if (reason.length < 5) {
      setReasonError("Reason must be at least 5 characters long");
      return false;
    } else if (reason.length > 100) {
      setReasonError("Reason cannot exceed 100 characters");
      return false;
    }
    setReasonError("");
    return true;
  };

  // Handle VM selection change
  const handleVmSelectionChange = (vmId) => {
    setSelectedVmid(vmId);
    if (onVmSelect) {
      onVmSelect(vmId);
    }
    setDropdownOpen(false); // Close dropdown after selection
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setVmidSearch(query);
    if (onSearch) {
      onSearch(query);
    }
  };

  // Filter VM list based on search query
  const filteredVmList = vmList.filter(vm => {
    if (!vmidSearch.trim()) return true;
    
    const searchLower = vmidSearch.toLowerCase();
    return (
      (vm.vmId && vm.vmId.toString().toLowerCase().includes(searchLower)) ||
      (vm.id && vm.id.toString().toLowerCase().includes(searchLower)) ||
      (vm.name && vm.name.toLowerCase().includes(searchLower))
    );
  });

  // Handle dropdown open/close
  const handleDropdownOpen = () => {
    setDropdownOpen(true);
    // Load initial VM list when dropdown opens
    if (onSearch) {
      onSearch("");
    }
    setVmidSearch(""); // Reset search when opening dropdown
  };

  const handleDropdownClose = () => {
    setDropdownOpen(false);
    setVmidSearch(""); // Reset search when closing dropdown
  };

  // Prevent dropdown from closing when clicking on search field
  const handleSearchFieldClick = (e) => {
    e.stopPropagation();
  };

  // Handle key down in search field to prevent dropdown from closing
  const handleSearchKeyDown = (e) => {
    e.stopPropagation();
  };

  const handleUnmapClick = () => {
    if (uidDetails && uidDetails.mappingState === "Unmapped") {
      alert("This UID is already unmapped!");
      return;
    }

    // Validate reason before proceeding
    if (!validateReason()) {
      return;
    }

    setShowConfirmation(true);
  };

  // FIXED: Modified handleMapClick to open confirmation popup
  const handleMapClick = () => {
    if (!selectedVmid) {
      alert("Please select a VMID");
      return;
    }

    // Validate reason for mapping (optional for mapping, but you can add if needed)
    if (reason && !validateReason()) {
      return;
    }

    // Call the parent's handleMap function which will open confirmation popup
    if (onMap) {
      onMap(selectedVmid, reason || "Mapping UID to VM");
    }
  };

  // NEW: Handle confirmed map action
  const handleConfirmMap = async () => {
    setMapLoading(true);
    try {
      if (onConfirmedMap) {
        await onConfirmedMap();
        // Success state is now managed by parent component
      }
    } catch (error) {
      console.error("Map error:", error);
    } finally {
      setMapLoading(false);
    }
  };

  // NEW: Handle cancel map
  const handleCancelMap = () => {
    if (onCancelMap) {
      onCancelMap();
    }
  };

  const handleConfirmUnmap = async () => {
    setShowConfirmation(false);
    setUnmapLoading(true);

    try {
      if (onUnmap) {
        await onUnmap(reason);
        // Success state is now managed by parent component
      }
    } catch (error) {
      console.error("Unmap error:", error);
    } finally {
      setUnmapLoading(false);
    }
  };

  const handleCancelUnmap = () => {
    setShowConfirmation(false);
  };

  const handleQRClick = async (e) => {
    e.preventDefault();
    
    try {
      setShowQRModal(true);
      setIsCapturing(true);
      
      setTimeout(() => {
        captureAndDownloadModal();
      }, 1000);
      
    } catch (error) {
      console.error("Download error:", error);
      setSnackbarMessage("Failed to download QR Page");
      setSnackbarOpen(true);
      setIsCapturing(false);
    }
  };

  const captureAndDownloadModal = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const modalElement = document.querySelector('[role="dialog"]');
      
      if (!modalElement) {
        throw new Error("Modal not found");
      }

      const canvas = await html2canvas(modalElement, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#FFD4B2',
        logging: false,
        removeContainer: true
      });

      const dataUrl = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `QR_Page_${selectedUid}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbarMessage("QR Page downloaded successfully!");
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error("Error capturing modal:", error);
      setSnackbarMessage("Failed to download QR Page");
      setSnackbarOpen(true);
    } finally {
      setIsCapturing(false);
    }
  };

  const handleQRClose = () => {
    if (!isCapturing) {
      setShowQRModal(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Format date function using moment for UTC to IST conversion
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      // Convert UTC to IST using moment-timezone
      const istDate = moment.utc(dateString).tz('Asia/Kolkata');
      return istDate.format('MMMM D, YYYY, hh:mm A');
    } catch (error) {
      console.error("Date formatting error:", error);
      return dateString;
    }
  };

  // Check if we should show success popup
  const showSuccess = mapSuccess || unmapSuccess;
  const isMapped = uidDetails?.uidInfo?.mappingState === "Mapped";
  const isUnmapped = uidDetails?.uidInfo?.mappingState === "Unmapped";

  if (loading) {
    return (
      <div className="uid-unmap-container">
        <Sidebar />
        <div className="uid-unmap-content">
          <div className="loading-container">
            <CircularProgress />
            <p>Loading UID details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!uidDetails) {
    return (
      <div className="uid-unmap-container">
        <Sidebar />
        <div className="uid-unmap-content">
          <Alert severity="warning">No UID details found</Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="uid-unmap-container">
      <Sidebar />
      <div className="uid-unmap-content">
        {/* Header */}
        <div className="uid-unmap-header">
          <div style={{ display: "flex", justifyContent: "center", gap: "10px", alignItems: "center" }}>
            <Button className="back-btn" onClick={() => navigate(-1)}>
              <img src={leftArrow} alt="Back" />
            </Button>
            <h1 className="page-title">
              {isMapped ? "Unmapping UID - VMID" : "Map UIDs-VMIDs"}
            </h1>
          </div>
          <div>
            <Button variant="outlined" className="timeline-btn" onClick={handleUidTimeline}>
              UID Timeline
            </Button>
          </div>
        </div>

        {/* UID and VMID Selection Section - Only show when Unmapped */}
        {isUnmapped && (
          <div className="selection-sections-container">
            {/* UID Selection Section */}
            <div className="selection-section">
              <div className="selection-container">
                <FormControl fullWidth variant="standard">
                  <InputLabel>Select UID</InputLabel>
                  <Select
                    value={selectedUid}
                    onChange={(e) => setSelectedUid(e.target.value)}
                    label="Select UID"
                    IconComponent={() => <img src={dropdownArrow} alt="▼" className="dropdown-icon" />}
                  >
                    <MenuItem value={selectedUid}>
                      {selectedUid}
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>

            {/* VMID Selection Section */}
            <div className="selection-section">
              <div className="selection-container">
                <FormControl fullWidth variant="standard">
                  <InputLabel>Select VMID</InputLabel>
                  <Select
                    value={selectedVmid}
                    onChange={(e) => handleVmSelectionChange(e.target.value)}
                    onOpen={handleDropdownOpen}
                    onClose={handleDropdownClose}
                    open={dropdownOpen}
                    label="Select VMID"
                    IconComponent={() => <img src={dropdownArrow} alt="▼" className="dropdown-icon" />}
                    displayEmpty
                    MenuProps={{
                      // This prevents the menu from closing when clicking inside
                      disableAutoFocusItem: true,
                      // Add custom styles to ensure proper display
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    {/* Search Field - Only show when dropdown is open */}
                    {dropdownOpen && (
                      <div 
                        className="search-field-container" 
                        onClick={handleSearchFieldClick}
                        onKeyDown={handleSearchKeyDown}
                      >
                        <TextField
                          fullWidth
                          variant="standard"
                          placeholder="Search by VMID, Name or ID..."
                          value={vmidSearch}
                          onChange={handleSearchChange}
                          inputRef={searchInputRef}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <img src={searchIcon} alt="Search" className="search-icon" />
                              </InputAdornment>
                            ),
                            disableUnderline: true,
                          }}
                          autoFocus
                          // Prevent the TextField from closing the dropdown
                          onKeyDown={handleSearchKeyDown}
                          onClick={handleSearchFieldClick}
                        />
                        {searchLoading && (
                          <div style={{ padding: '8px', textAlign: 'center' }}>
                            <CircularProgress size={20} />
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Filtered VM List */}
                    {filteredVmList.length > 0 ? (
                      filteredVmList.map((vm) => (
                        <MenuItem 
                          key={vm.id} 
                          value={vm.id}
                          onClick={() => handleVmSelectionChange(vm.id)}
                         
                        >
                          <div className="vmid-menu-item">
                            <span className="vmid-id">{vm.vmId || vm.id}</span>
                            <span className="vmid-name">{vm.name}</span>
                          </div>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        {vmidSearch ? "No VMIDs found for your search" : "No VMIDs available"}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>
        )}

        {/* Rest of your component remains the same */}
        {/* UID and VMID Cards Section */}
        <div className="uid-vmid-section">
          {/* UID Card */}
          <div className="card uid-card">
            <div className="card-header">
              <div className="card-title-with-icon">
                <img src={uidIcon} alt="UID" className="card-title-icon" />
                <h2>UID: {selectedUid}</h2>
              </div>
              <span className={`uid-detils-status ${uidDetails.uidInfo.mappingState?.toLowerCase()}`}>
                {uidDetails.uidInfo.mappingState}
              </span>
            </div>
            
            <div className="uid-card-detail-item">
              <img src={calendarIcon} alt="Created" className="detail-icon" />
              <p><strong>Created On:</strong> {formatDate(uidDetails.uidInfo.createdAt)}</p>
            </div>
            
            <div className="uid-card-detail-item">
              <img src={calendarIcon} alt="Updated" className="detail-icon" />
              <p><strong>Last Mapped:</strong> {formatDate(uidDetails.uidInfo.lastUpdatedAt)}</p>
            </div>
            
            <div className="uid-card-detail-item">
              <img src={userIcon} alt="Admin" className="detail-icon" />
              <p><strong>Admin Name:</strong> {uidDetails.uidInfo.createdByName}</p>
            </div>
            
            <div className="uid-card-detail-item">
              <img src={qrIcon} alt="QR" className="detail-icon" />
              <p>
                <strong>QR:</strong>{" "}
                <a 
                  href="#" 
                  onClick={handleQRClick}
                  style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                  disabled={isCapturing}
                >
                  {isCapturing ? "Downloading..." : `QR_${selectedUid}`}
                </a>
              </p>
            </div>
          </div>

          {/* VMID Card */}
          <div className="card vmid-card">
            <div className="card-header">
              <div className="card-title-with-icon">
                <img src={vmidIcon} alt="VMID" className="card-title-icon" />
                <h2>
                  {isMapped ? (
                    <>
                      VMID: {uidDetails.vmInfo.id || "0002"}
                      {uidDetails.vmName && ` - ${uidDetails.vmName}`}
                    </>
                  ) : selectedVmDetails ? (
                    <>
                      VMID: {selectedVmDetails.id}
                      {selectedVmDetails.name && ` - ${selectedVmDetails.name}`}
                    </>
                  ) : (
                    "VM Information"
                  )}
                </h2>
              </div>
              {isMapped && <span className="status mapped">{uidDetails.uidInfo.mappingState}</span>}
            </div>

            {isMapped ? (
              // Show mapped VM details
              <>
                <div className="uid-card-detail-item">
                  <img src={userIcon} alt="Name" className="detail-icon" />
                  <p><strong>Name:</strong> {uidDetails.vmInfo.name || "Test Machine 1234"}</p>
                </div>
                
                <div className="uid-card-detail-item">
                  <img src={calendarIcon} alt="Created" className="detail-icon" />
                  <p><strong>Created On:</strong> {formatDate(uidDetails.vmInfo.createdAt) || "July 7, 2025, 02:00 PM"}</p>
                </div>
                
                <div className="uid-card-detail-item">
                  <img src={statusIcon} alt="Status" className="detail-icon" />
                  <p><strong>Last Mapped To:</strong> {uidDetails.mappingState || "N/A"}</p>
                </div>
                
                <div className="uid-card-detail-item">
                  <img src={userIcon} alt="Admin" className="detail-icon" />
                  <p><strong>Admin Name:</strong> {uidDetails.vmInfo.createdByName || "Pattu Patwardan"}</p>
                </div>
                
                <div className="uid-card-detail-item">
                  <img src={locationIcon} alt="Address" className="detail-icon" />
                  <p><strong>Address:</strong> {uidDetails.vmInfo.address || "N/A"}</p>
                </div>
              </>
            ) : selectedVmDetails ? (
              // Show selected VM details
              <>
                <div className="uid-card-detail-item">
                  <img src={userIcon} alt="Name" className="detail-icon" />
                  <p><strong>Name:</strong> {selectedVmDetails.name || "N/A"}</p>
                </div>
                
                <div className="uid-card-detail-item">
                  <img src={calendarIcon} alt="Created" className="detail-icon" />
                  <p><strong>Created On:</strong> {formatDate(selectedVmDetails.created_at)}</p>
                </div>
                
                {/* <div className="uid-card-detail-item">
                  <img src={statusIcon} alt="Status" className="detail-icon" />
                  <p><strong>Status:</strong> {selectedVmDetails.operational_status || "Active"}</p>
                </div> */}
                
                <div className="uid-card-detail-item">
                  <img src={userIcon} alt="Admin" className="detail-icon" />
                  <p><strong>Admin Name:</strong> {selectedVmDetails.admin_name || "N/A"}</p>
                </div>
                
                <div className="uid-card-detail-item">
                  <img src={locationIcon} alt="Address" className="detail-icon" />
                  <p><strong>Address:</strong> {selectedVmDetails.address?.street || "N/A"},{selectedVmDetails.address?.city || "N/A"}, {selectedVmDetails.address?.state || "N/A"}</p>
                </div>
                
                {/* <div className="uid-card-detail-item">
                  <img src={locationIcon} alt="Location" className="detail-icon" />
                  <p><strong>Location:</strong> {selectedVmDetails.address?.city || "N/A"}, {selectedVmDetails.address?.state || "N/A"}</p>
                </div> */}
              </>
            ) : (
              // No VM selected state
              <div className="no-selection-message">
                <img src={vmidIcon} alt="VMID" className="no-selection-icon" />
                <p><strong>Select a VMID to view details</strong></p>
              </div>
            )}
          </div>
        </div>

        {/* Reason Section - Only show for Unmapping */}
        {isMapped && (
          <div className="reason-box">
            <h3>Reason For Unmapping</h3>
            <TextField
              fullWidth
              value={reason}
              onChange={handleReasonChange}
              variant="standard"
              placeholder="Enter reason for unmapping (5-100 characters)"
              error={!!reasonError}
              helperText={reasonError || `${reason.length}/100 characters`}
              inputProps={{
                maxLength: 100
              }}
            />
          </div>
        )}

        {/* Action Button - Different based on mapping state */}
        <div className="action-btn-section">
          {isMapped ? (
            <Button
              variant="contained"
              className="unmap-btn"
              onClick={handleUnmapClick}
              disabled={unmapLoading || reason.trim() === "" || reason.length < 5 || reason.length > 100}
            >
              {unmapLoading ? "Unmapping..." : "Unmap Data"}
            </Button>
          ) : (
            <Button
              variant="contained"
              className="map-btn"
              onClick={handleMapClick}
              disabled={mapLoading || !selectedVmid}
            >
              {mapLoading ? "Mapping..." : "Map Data"}
            </Button>
          )}
        </div>

        {/* QR Code Modal */}
        <QRCodeModal
          open={showQRModal}
          onClose={handleQRClose}
          uid={selectedUid}
        />

        {/* Unmap Confirmation Popup - Only for Unmapping */}
        {showConfirmation && isMapped && (
          <div className="popup-overlay">
            <div className="confirmation-popup">
              <img src={awaitingicon} alt="" style={{ height: "80px", width: "80px", marginBottom: "10px" }} />
              <h3>Are You Sure You Want To Unmap</h3>
              <p> {uidDetails.uidInfo.uid}- {uidDetails.vmInfo.id} ?</p>
              <div className="confirmation-buttons">
                <Button className="cancel-btn" onClick={handleCancelUnmap}>
                  No
                </Button>
                <Button className="confirm-btn" onClick={handleConfirmUnmap}>
                  Yes
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Map Confirmation Popup - NEW */}
        {mapConfirmationOpen && (
          <div className="popup-overlay">
            <div className="confirmation-popup">
              <img src={awaitingicon} alt="" style={{ height: "80px", width: "80px", marginBottom: "10px" }} />
              <h3>Are You Sure You Want To Map</h3>
              <p>{selectedUid} to VMID {pendingMapData?.vmid}?</p>
              <div className="confirmation-buttons">
                <Button className="cancel-btn" onClick={handleCancelMap} disabled={mapLoading}>
                  No
                </Button>
                <Button 
                  className="confirm-btn" 
                  onClick={handleConfirmMap}
                  disabled={mapLoading}
                >
                  {mapLoading ? "Mapping..." : "Yes"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Success Dialog - NEW */}
        {showSuccessDialog && (
          <div className="popup-overlay">
            <div className="success-popup">
              <img src={successIcon} alt="Success" style={{ height: "80px", width: "80px", marginBottom: "10px" }} />
              <h3 style={{ color: '#4CAF50', marginBottom: '10px' }}>Success!</h3>
              <p style={{ marginBottom: '20px', fontSize: '16px' }}>{successMessage}</p>
              <div className="uid-success-buttons">
                <Button 
                  className="uid-success-ok-btn" 
                  onClick={handleSuccessOk}
                  variant="contained"
                  style={{ backgroundColor: '#4CAF50', color: 'white' }}
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Snackbar for download success/error */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </div>
    </div>
  );
};

export default UidDashboardDetailsComponent;